import { cn } from '@utils/helpers';
import { InfoIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useFragment } from 'react-relay';
import { CategoryInfo } from '../categoryInfo';
import { DetailFilterItem } from '../detailFilterItem';
import { sortYearsDescending } from '../sortYears';
import { detailFilterListCategoryFragment } from './DetailFilterListCategory.relay';
import type { DetailFilterListCategoryPropsType } from './types';

const DetailFilterListCategory: React.FC<DetailFilterListCategoryPropsType> = (props) => {
  const category = useFragment(detailFilterListCategoryFragment, props.category);

  // Year items arrive from the server in an unspecified order. Sort them so the
  // most recent year is first, matching the dropdown variant. Other list
  // categories keep their server order.
  const filterItems = useMemo(() => {
    if (category.col === 'year') {
      return [...category.filteritems].sort(sortYearsDescending);
    }
    return category.filteritems;
  }, [category.col, category.filteritems]);

  const [infoVisible, setInfoVisible] = useState(false);

  const aggregateFilterVals = useCallback(
    (label: string, filterVal: string) => {
      let tempFilterVals = [...props.filterVals];

      if (props.radio) {
        tempFilterVals = [filterVal];
      } else {
        const filterValIndex = tempFilterVals.indexOf(filterVal);

        if (filterValIndex > -1) {
          tempFilterVals.splice(filterValIndex, 1);
        } else {
          tempFilterVals.push(filterVal);
        }
      }

      props.filterHandlerFn(category.col, tempFilterVals);
    },
    [props.filterVals, props.radio, props.filterHandlerFn, category.col],
  );

  const toggleInfo = useCallback((): void => {
    setInfoVisible((prev) => !prev);
  }, []);

  const closeInfo = useCallback((): void => {
    setInfoVisible(false);
  }, []);

  const showCategoryName = category.categoryname || category.info;

  return (
    <div className="grid grid-cols-1 desktop:grid-cols-[1fr_3fr] gap-2">
      {showCategoryName ? (
        <div className="flex items-center gap-2">
          <span>{category.categoryname}</span>
          {category.info ? <InfoIcon onClick={toggleInfo} className={cn('text-gray-300 size-5', infoVisible && 'fill-accent text-white')} /> : null}
        </div>
      ) : null}
      <div className="flex gap-1 max-[600px]:flex-col max-[600px]:gap-0">
        {filterItems.map((item) => (
          <DetailFilterItem
            key={item.label}
            filterHandlerFn={aggregateFilterVals}
            filterItem={item}
            isRadio={props.radio}
            inputGroupName={props.inputGroupName}
            selected={props.filterVals.indexOf(item.filtervalue) > -1}
          />
        ))}
      </div>
      {showCategoryName && category.info ? <CategoryInfo info={category.info} close={closeInfo} visible={infoVisible} /> : null}
    </div>
  );
};

export { DetailFilterListCategory };
